// Lazy-Teil der Registry: lädt apps/web/src/modules/<key>/module.tsx und nimmt den Beitrag der
// Plattform. Ein Modul, das beim Laden scheitert, fällt weg (Fehler in `failed`), der Rest läuft.
import type { ModuleKey } from '@bar/contracts/modules';
import { MODULE_KEYS } from '@bar/contracts/modules';
import type { ModuleContributions, PlatformContribution, PlatformKey } from './types';

type Loader = () => Promise<{ default: ModuleContributions }>;

const files = import.meta.glob<{ default: ModuleContributions }>('../modules/*/module.tsx');

function keyOf(path: string): string {
  return path.split('/').at(-2) ?? path;
}

export interface LoadedModule<P extends PlatformKey> {
  key: ModuleKey;
  contribution: PlatformContribution<P>;
}

export interface LoadedRegistry<P extends PlatformKey> {
  platform: P;
  modules: LoadedModule<P>[];
  failed: { key: string; error: unknown }[];
}

export async function loadContributions<P extends PlatformKey>(
  platform: P,
  loaders: Record<string, Loader> = files,
): Promise<LoadedRegistry<P>> {
  const entries = Object.entries(loaders)
    .map(([path, load]) => ({ key: keyOf(path), load }))
    .filter((entry) => (MODULE_KEYS as readonly string[]).includes(entry.key))
    .sort(
      (a, b) => MODULE_KEYS.indexOf(a.key as ModuleKey) - MODULE_KEYS.indexOf(b.key as ModuleKey),
    );
  const results = await Promise.allSettled(entries.map((entry) => entry.load()));
  const registry: LoadedRegistry<P> = { platform, modules: [], failed: [] };
  results.forEach((result, index) => {
    const key = entries[index]!.key as ModuleKey;
    if (result.status === 'rejected') {
      console.error(`[registry] Modul ${key} nicht geladen`, result.reason);
      registry.failed.push({ key, error: result.reason });
      return;
    }
    const contribution = result.value.default?.[platform] as PlatformContribution<P> | undefined;
    if (contribution) registry.modules.push({ key, contribution });
  });
  return registry;
}
